import type { api } from "@healthos/schemas";
import {
  type AvoidanceCohort,
  avoidancePerEnrollee,
  effectiveMemberMonths,
} from "@healthos/shared";
import { releaseWillChange } from "@healthos/ui";
import { CaretDown } from "@phosphor-icons/react";
import * as React from "react";
import { formatNumber, formatPct, OPS_PANEL } from "../ops-layout";

export { formatNumber, formatPct, OPS_PANEL };

/**
 * Shared formatters and small atoms reused across the operations
 * dashboard tabs. Wire types live in @healthos/schemas (ADR-0010) and
 * are re-exported here under the names the tab modules already import.
 */

export type BleedItem = api.OperationsBleedItem;
export type PersonaPopulation = api.OperationsPersonaPopulation;
export type TrajectoryPoint = api.OperationsTrajectoryPoint;
export type ProjectionPoint = api.OperationsProjectionPoint;
export type ROIDefaults = api.OperationsRoiDefaults;
export type Citation = api.OperationsCitation;
export type OperationsMode = api.OperationsMode;
export type TriageSeverity = api.OperationsTriageSeverity;
export type TriageKind = api.OperationsTriageKind;
export type TriageEntry = api.OperationsTriageEntry;
export type OperationsTriage = api.OperationsTriage;
export type OperationsBenchmarks = api.OperationsBenchmarks;
export type OperationsMethodology = api.OperationsMethodology;
export type OperationsEngagement = api.OperationsEngagement;
export type OperationsHabitFormation = api.OperationsHabitFormation;
export type OperationsPushHabituation = api.OperationsPushHabituation;
export type OpsCalendarKind = api.OpsCalendarKind;
export type OpsCalendarEvent = api.OpsCalendarEvent;
export type OperationsKeyDates = api.OperationsKeyDates;
export type DashboardPayload = api.OperationsDashboardResponse;
export type OperationsMe = api.OperationsMeResponse;
export type SavedScenario = api.OperationsSavedScenario;

// ---------------------------------------------------------------------------
// Format helpers - every numeral lands in tabular form.
// ---------------------------------------------------------------------------

const USD_FORMAT = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

export function formatUSD(amount: number, opts: { compact?: boolean } = {}): string {
  if (opts.compact && Math.abs(amount) >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(1)}M`;
  }
  if (opts.compact && Math.abs(amount) >= 1_000) {
    return `$${(amount / 1_000).toFixed(0)}K`;
  }
  return USD_FORMAT.format(amount);
}

export function hasCap(cap: string, me: OperationsMe | null): boolean {
  if (!me) return false;
  return me.capabilities.includes(cap);
}

// Recharts needs concrete color strings, not var(...): resolve tokens lazily
// at first render (module load can beat the stylesheet and read "").
const tokenColorCache = new Map<string, string>();
function tokenColor(name: string): string {
  if (typeof window === "undefined") return "";
  const cached = tokenColorCache.get(name);
  if (cached) return cached;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (value) tokenColorCache.set(name, value); // never cache "" so a pre-stylesheet read can recover
  return value;
}

export const CHART_INK = {
  get axis() {
    return tokenColor("--color-gray-500");
  },
  get reference() {
    return tokenColor("--color-gray-400");
  },
  get surface() {
    return tokenColor("--color-mist");
  },
  get intervention() {
    return tokenColor("--color-teal-600");
  },
  get interventionFill() {
    return tokenColor("--color-teal-300");
  },
  get baseline() {
    return tokenColor("--color-leaf");
  },
  get warning() {
    return tokenColor("--color-attention-timely-ink");
  },
};

// ---------------------------------------------------------------------------
// ROI math - used by Hero (overview tab) and KpiStrip + ROIPresetCards
// (cohort tab). Lives here because it is genuinely shared, even though
// the slider UI is owned by cohort.tsx.
// ---------------------------------------------------------------------------

export interface ROIInputs {
  headcount: number;
  pepmPrice: number;
  enrolmentShare: number;
  pmpmAvoidedPerParticipant: number;
  /** Share of sign-ups still engaged at twelve months. Omitted means perfect. */
  retentionShare?: number;
  /** 0 = random draw from the workforce, 1 = fill from the richest cohort down. */
  targetingStrength?: number;
  /** The condition mix targeting draws from. Omitted disables targeting. */
  cohorts?: readonly AvoidanceCohort[];
}

export function computeROI(inputs: ROIInputs) {
  const enrolledCount = Math.round(inputs.headcount * inputs.enrolmentShare);
  const annualCost = inputs.headcount * inputs.pepmPrice * 12;
  // A fee is charged every month; avoidance only accrues while a member is
  // still engaged, and only enrollees carrying a condition avoid anything.
  const memberMonths = effectiveMemberMonths(inputs.retentionShare ?? 1);
  // Whenever the mix is available it drives this, including at zero targeting
  // where it returns the population-weighted average. Falling back to the
  // rounded constant only at zero made the curve jump the moment a caller
  // nudged the targeting slider off its stop.
  const perMemberPmpm = inputs.cohorts
    ? avoidancePerEnrollee(inputs.cohorts, inputs.enrolmentShare, inputs.targetingStrength ?? 0)
    : inputs.pmpmAvoidedPerParticipant;
  const annualSavings = enrolledCount * perMemberPmpm * memberMonths;
  const netSavings = annualSavings - annualCost;
  const roiMultiple = annualCost === 0 ? 0 : annualSavings / annualCost;
  const monthlyCost = annualCost / 12;
  const monthlySavings = annualSavings / 12;
  const paybackMonths =
    monthlySavings > monthlyCost
      ? monthlyCost / (monthlySavings - monthlyCost) + 1
      : Number.POSITIVE_INFINITY;
  const discount = 0.06;
  let npv3yr = 0;
  for (let year = 1; year <= 3; year++) {
    npv3yr += netSavings / (1 + discount) ** year;
  }
  return {
    enrolledCount,
    annualCost,
    annualSavings,
    netSavings,
    roiMultiple,
    paybackMonths,
    npv3yr,
    // The two intermediates a caller would otherwise recompute to explain the
    // figure, returned so the explanation cannot drift from the arithmetic.
    memberMonths,
    perMemberPmpm,
  };
}

/**
 * The PEPM at which modeled savings exactly cover the fee. This is the number
 * an ops team can act on: it converts "is the return positive" into "at what
 * price is it positive", which is a contract lever rather than a verdict.
 */
export function breakEvenPepm(defaults: ROIDefaults): number {
  if (defaults.headcount <= 0) return 0;
  const { annualSavings } = computeROI({ ...roiInputs(defaults), pepmPrice: 0 });
  return annualSavings / (defaults.headcount * 12);
}

/**
 * The one input set every ROI figure on the dashboard is built from, so a
 * retention or targeting assumption cannot apply to one tile and not another.
 */
/**
 * The served condition mix, or undefined when none came back. An empty array
 * is not a mix of nothing: it would drive the targeting maths to zero
 * avoidance, where the intent is to fall back to the flat coefficient.
 */
export function roiCohorts(defaults: ROIDefaults): readonly AvoidanceCohort[] | undefined {
  return defaults.avoidanceCohorts?.length ? defaults.avoidanceCohorts : undefined;
}

export function roiInputs(defaults: ROIDefaults): ROIInputs {
  return {
    headcount: defaults.headcount,
    pepmPrice: defaults.pepmPrice,
    enrolmentShare: defaults.year1EnrolmentShare,
    pmpmAvoidedPerParticipant: defaults.pmpmAvoidedPerParticipant,
    retentionShare: defaults.year1RetentionShare ?? 1,
    targetingStrength: defaults.targetingStrength ?? 0,
    cohorts: roiCohorts(defaults),
  };
}

/**
 * Return across the contracted PEPM band rather than at a single price.
 * `best` uses the low end of the band, `worst` the high end.
 */
export function roiRange(defaults: ROIDefaults) {
  const base = roiInputs(defaults);
  const at = (pepmPrice: number) => computeROI({ ...base, pepmPrice });
  const low = defaults.pepmPriceLow ?? defaults.pepmPrice;
  const high = defaults.pepmPriceHigh ?? defaults.pepmPrice;
  return {
    best: at(low),
    worst: at(high),
    low,
    high,
    breakEven: breakEvenPepm(defaults),
  };
}

/**
 * The model at its stated assumptions: the base case, and the figure the rest
 * of the dashboard anchors on. Named for what it is rather than for a course
 * of action, because nothing here is something an operator does.
 */
export function baseCaseROI(defaults: ROIDefaults) {
  return computeROI(roiInputs(defaults));
}

// ---------------------------------------------------------------------------
// Stagger wrapper. Wraps a child with `fade-in-up` keyframe + delay so
// the page composes itself instead of slamming in. Tokens.css ships
// the keyframe; we just feed CSS animation props inline.
// ---------------------------------------------------------------------------

export function Stagger({
  delay,
  children,
  className,
}: {
  delay: number;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={className}
      style={{
        animation: "fade-in-up 480ms var(--ease-out-expo) both",
        animationDelay: `${delay}ms`,
        willChange: "transform, opacity",
      }}
      onAnimationEnd={(event) => {
        // Drop the will-change hint once the one-shot reveal completes.
        releaseWillChange(event, "fade-in-up");
      }}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modeled chip - the per-number honesty marker. Every dollar figure that
// comes from the benchmark model carries one; measured numbers never do.
// When the claims feed lands, drop the chip per metric.
// ---------------------------------------------------------------------------

export function ModeledChip() {
  return (
    <span
      className="ml-1.5 inline-flex cursor-help items-center rounded-full border border-border-strong border-dashed px-1.5 py-px align-middle font-mono text-2xs uppercase tracking-[0.12em] text-ink-quaternary"
      title="Modeled from published benchmarks until your claims feed is connected"
    >
      modeled
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sparkline - small inline trend used by Hero stats and KPI tiles.
// ---------------------------------------------------------------------------

export function Sparkline({
  values,
  color,
}: {
  values: number[];
  color: "teal" | "leaf" | "amber";
}) {
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const w = 64;
  const h = 18;
  const pts = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * w;
      const y = h - ((v - min) / range) * h;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const stroke =
    color === "leaf"
      ? "var(--color-leaf-dark)"
      : color === "amber"
        ? "var(--color-attention-timely-ink)"
        : "var(--color-teal-600)";
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img">
      <title>Trend</title>
      <polyline
        fill="none"
        stroke={stroke}
        strokeWidth="1.5"
        points={pts}
        pathLength="1"
        className="animate-sparkline-draw motion-reduce:[animation:none]"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// DisclosurePanel - an expanding card, on the mechanics the attention card
// already uses for "More context": a button carrying aria-expanded /
// aria-controls, a rotating caret, and the 0fr -> 1fr grid trick so height
// animates without transitioning `height`.
// ---------------------------------------------------------------------------

/** Chrome and Firefox 139+ have hidden="until-found"; Safari does not yet. */
function supportsHiddenUntilFound(): boolean {
  return (
    typeof document !== "undefined" && document.body != null && "onbeforematch" in document.body
  );
}

/**
 * The collapse mechanics, shared by every disclosure on the dashboard.
 *
 * Worth having in one place because the collapsed-body handling is subtle and
 * easy to get wrong: React coerces a `hidden` prop to a boolean, so the
 * attribute has to be set imperatively, and Safari still has no
 * hidden="until-found" and needs inert instead.
 *
 * The 0fr -> 1fr row is NOT transitioned, and that is deliberate. Chrome does
 * not interpolate grid-template-rows between fr values here: it holds the
 * start value, so the panel animated from nothing to nothing and opened to a
 * zero-height body. Reproduced with a bare div, no React and no containment
 * involved. Removing the transition makes the disclosure correct and instant;
 * the body still fades and slides, which reads fine and does animate. Restore
 * the height animation only against a browser that demonstrably interpolates
 * it, or move to interpolate-size with height:auto.
 */
export function useDisclosure(defaultOpen: boolean) {
  const [open, setOpen] = React.useState(defaultOpen);
  const panelId = React.useId();
  const bodyRef = React.useRef<HTMLDivElement>(null);

  /**
   * Toggle, and on the way open un-hide the body FIRST.
   *
   * hidden="until-found" is content-visibility: hidden, which applies size
   * containment, so the body contributes zero height. The row animates
   * 0fr -> 1fr and the browser resolves what 1fr means when the transition
   * starts. Removing the attribute from an effect is too late: React has
   * already committed the new row and the target resolved against a
   * zero-height body, so the panel animated from nothing to nothing. Doing it
   * here, in the handler, means the body has its height before the style
   * changes. The effect below still owns the closing direction.
   */
  const toggle = React.useCallback(() => {
    setOpen((wasOpen) => {
      if (!wasOpen) bodyRef.current?.removeAttribute("hidden");
      return !wasOpen;
    });
  }, []);

  React.useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    // Set imperatively: React coerces `hidden` to a boolean, so a `hidden`
    // prop renders hidden="" and hides the body from find-in-page outright.
    if (open) {
      body.removeAttribute("hidden");
      body.removeAttribute("inert");
      return;
    }
    if (supportsHiddenUntilFound()) {
      body.removeAttribute("inert");
      body.setAttribute("hidden", "until-found");
    } else {
      // Safari has no until-found as of 2026-02, and treats the value as
      // invalid, which means display:none. That is not findable either and it
      // cannot animate open, so fall back to inert: same untabbable collapsed
      // body the panel had before, still animating.
      body.removeAttribute("hidden");
      body.setAttribute("inert", "");
    }
  }, [open]);

  React.useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    // Fired when find-in-page matches inside the hidden body; open the panel so
    // the header state matches what the browser just revealed.
    const onBeforeMatch = () => setOpen(true);
    body.addEventListener("beforematch", onBeforeMatch);
    return () => body.removeEventListener("beforematch", onBeforeMatch);
  }, []);

  return { open, setOpen, toggle, panelId, bodyRef };
}

/**
 * The shell every disclosure on this page shares: the labelled trigger, the
 * caret, the aria wiring, and the grid body `useDisclosure` drives. Only the
 * chrome and the header content differ between callers, so those are the only
 * things passed in.
 *
 * Deliberately no role="region"/aria-labelledby on the body. APG pairs those,
 * and warns against the region role past roughly six simultaneously expandable
 * panels; this page renders eight, so they would add eight landmarks for no
 * gain. The disclosure pattern requires neither.
 */
function DisclosureShell({
  as: Tag = "section",
  className,
  triggerClassName,
  bodyClassName,
  caretClassName = "",
  header,
  defaultOpen = false,
  children,
}: {
  as?: "section" | "footer";
  className: string;
  triggerClassName: (open: boolean) => string;
  bodyClassName?: (open: boolean) => string;
  caretClassName?: string;
  header: (open: boolean) => React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const { open, toggle, panelId, bodyRef } = useDisclosure(defaultOpen);

  return (
    <Tag className={className}>
      <h2>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={toggle}
          className={`cursor-pointer text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600/40 focus-visible:ring-inset ${triggerClassName(open)}`}
        >
          {header(open)}
          <CaretDown
            aria-hidden
            size={14}
            weight="bold"
            className={`shrink-0 text-ink-quaternary transition-transform duration-300 ease-out motion-reduce:transition-none ${caretClassName} ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>
      </h2>
      <div id={panelId} className="grid" style={{ gridTemplateRows: open ? "1fr" : "0fr" }}>
        <div className="overflow-hidden">
          <div
            ref={bodyRef}
            // Not `inert`: that keeps the body out of the tab order but also
            // hides it from find-in-page, and this page exists to be audited.
            // hidden="until-found" does both, and reveals on a match. The
            // attribute itself is set inside useDisclosure.
            className={bodyClassName?.(open)}
          >
            {children}
          </div>
        </div>
      </div>
    </Tag>
  );
}

export function DisclosurePanel({
  title,
  eyebrow,
  summary,
  defaultOpen = false,
  children,
}: {
  title: string;
  eyebrow: string;
  /** One line a buyer reads without expanding. An analyst expands. */
  summary?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  return (
    <DisclosureShell
      className={OPS_PANEL}
      defaultOpen={defaultOpen}
      // Square bottom while open, so the tint meets the body without a notch.
      triggerClassName={(open) =>
        `flex w-full items-start justify-between gap-4 px-5 py-3 hover:bg-cream/50 ${open ? "rounded-t-2xl" : "rounded-2xl"}`
      }
      caretClassName="mt-1"
      bodyClassName={(open) =>
        `border-border-default border-t px-5 py-4 text-sm text-ink-body leading-relaxed transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none ${
          open ? "translate-y-0 opacity-100" : "-translate-y-1 opacity-0"
        }`
      }
      header={(open) => (
        <span className="min-w-0">
          <span className="block font-semibold text-ink-primary text-sm">{title}</span>
          <span className="mt-0.5 block font-mono text-xs text-ink-tertiary uppercase tracking-wider">
            {eyebrow}
          </span>
          {summary ? (
            // Height collapses too; fading alone leaves a hole in the header.
            <span className="grid" style={{ gridTemplateRows: open ? "0fr" : "1fr" }}>
              <span className="overflow-hidden">
                <span
                  className="mt-1.5 block max-w-prose text-ink-secondary text-xs leading-relaxed transition-opacity duration-200 ease-out motion-reduce:transition-none"
                  style={{ opacity: open ? 0 : 1 }}
                >
                  {summary}
                </span>
              </span>
            </span>
          ) : null}
        </span>
      )}
    >
      {children}
    </DisclosureShell>
  );
}

// ---------------------------------------------------------------------------
// Sources footer - compact, mono. Rendered at the bottom of every tab.
// ---------------------------------------------------------------------------

export function SourcesFooter({ sources }: { sources: Citation[] }) {
  if (sources.length === 0) return null;
  return (
    // Collapsed by default. The list only grows, and a reader arriving at the
    // bottom of a tab is not looking for a bibliography; the one who wants it
    // knows to open it. Collapsed content stays reachable by find-in-page, so
    // searching for an author still surfaces it.
    <DisclosureShell
      as="footer"
      className="overflow-hidden rounded-2xl border border-border-default bg-cream/30"
      triggerClassName={() =>
        "flex w-full items-center justify-between gap-4 rounded-2xl px-5 py-4 hover:bg-cream/60"
      }
      header={() => (
        <span className="font-mono text-xs uppercase tracking-[0.18em]">
          <span className="font-semibold text-ink-primary">Sources</span>
          <span className="ml-2 text-ink-tertiary">
            {sources.length} cited, every number on this page
          </span>
        </span>
      )}
    >
      <ul className="grid gap-x-8 gap-y-1.5 px-5 pb-4 text-sm md:grid-cols-2">
        {sources.map((s) => (
          <li key={s.key} className="flex items-baseline justify-between gap-3 leading-snug">
            <a
              href={s.url}
              target="_blank"
              rel="noreferrer noopener"
              className="text-ink-brand hover:text-teal-800 hover:underline"
            >
              {s.label}
            </a>
            <span className="shrink-0 font-mono text-xs tabular-nums text-ink-quaternary">
              {s.year}
            </span>
          </li>
        ))}
      </ul>
    </DisclosureShell>
  );
}

// ---------------------------------------------------------------------------
// Skeleton shown while the dashboard payload is loading.
// ---------------------------------------------------------------------------

export function DashboardSkeleton() {
  return (
    <div className="p-6">
      <div className="mx-auto max-w-[1480px] space-y-3">
        <div className="h-12 animate-pulse rounded-lg bg-surface-track/50" />
        <div className="grid grid-cols-4 gap-2 md:grid-cols-8">
          {["a", "b", "c", "d", "e", "f", "g", "h"].map((slot) => (
            <div key={slot} className="h-20 animate-pulse rounded-lg bg-surface-track/50" />
          ))}
        </div>
        <div className="h-72 animate-pulse rounded-xl bg-surface-track/50" />
        <div className="h-48 animate-pulse rounded-xl bg-surface-track/50" />
      </div>
    </div>
  );
}
